import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Pencil, Check, RotateCcw, Plus, Bell, X } from 'lucide-react';
import api from '../lib/api';
import { ShelterTable } from './DashboardPage';

// ─── Asiakaskortti — kevyt Epic 18 (PoC 7, 15.9.2026) ──────────────────────────
// Perustiedot + Suojatilat. Ei Käyttäjät-välilehteä — käyttäjähallinta pysyy
// kokonaan Asetukset → Käyttäjät -sivulla (UsersPage.js), joka tukee jo
// tenant-kohtaista Admin-käyttäjän luontia ja poistoa. Ks. spec PoC 7 -osio.

// Suojatilan luonti kysyy taas nimen etukäteen (kumoaa osittain PoC 7 -päätöksen
// 19.9.2026) — 21.9.2026 koontinäyttö palautettiin näyttämään nimeä eikä
// sarjanumeroa ensisijaisena sarakkeena, joten oletusnimellä ("Uusi suojatila")
// luotu, vielä uudelleennimeämätön suojatila näkyisi jälleen suoraan
// koontinäytöllä geneerisenä — useampi luotu kerralla ei erottuisi toisistaan.
//
// #82 versio 10 (29.10.2026): valinta "Safe Digi käytössä". Pois päältä luodaan vain
// huollettava suojatila, joka näkyy vain SafePron Huolto-sivulla (ei asiakkaan käyttäjille).
function AddShelterButton({ tenantId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [safeDigi, setSafeDigi] = useState(true);
  const [creating, setCreating] = useState(false);

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post(`/api/superadmin/tenants/${tenantId}/shelters`, { name: name.trim(), safe_digi: safeDigi });
      navigate(`/suojatila/${data.id}`);
    } finally {
      setCreating(false);
    }
  }

  if (!open) {
    return (
      <div className="p-3 bg-gray-50 border-t border-gray-100">
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 bg-brand-700 text-white text-sm px-3 py-1.5 rounded-lg whitespace-nowrap hover:bg-brand-800"
        >
          <Plus size={14} />{t('dashboard.createShelter')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleCreate} className="p-3 bg-gray-50 border-t border-gray-100 flex flex-wrap gap-2 items-center">
      <input
        required
        autoFocus
        placeholder={t('tenants.shelter.namePlaceholder')}
        value={name}
        onChange={e => setName(e.target.value)}
        className="rounded-lg border border-gray-200 text-sm px-3 py-2 flex-1 min-w-[10rem] focus:outline-none focus:border-brand-500"
      />
      <label className="flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
        <input type="checkbox" checked={safeDigi} onChange={e => setSafeDigi(e.target.checked)} />
        {t('tenants.shelter.safeDigi')}
      </label>
      <button
        type="submit"
        disabled={creating || !name.trim()}
        className="bg-brand-700 hover:bg-brand-900 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap disabled:opacity-60"
      >
        {creating ? t('tenants.creating') : t('tenants.shelter.create')}
      </button>
      <button type="button" onClick={() => { setOpen(false); setName(''); setSafeDigi(true); }} className="text-gray-400 hover:text-gray-700">
        <X size={16} />
      </button>
    </form>
  );
}

// Microsoft-kirjautuminen (SSO, #28 28.9.2026). Superadmin tallentaa asiakkaan
// Entra-tenantin ID:n ja päättää pakotuksesta. Admin consent -linkki annetaan
// asiakkaan IT:lle, joka hyväksyy Safe Digi -sovelluksen omassa Entrassaan.
function SsoSection({ tenant, onSaved }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [required, setRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [clientId, setClientId] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get('/api/auth/sso/config')
      .then(({ data }) => setClientId(data?.client_id || null))
      .catch(() => setClientId(null));
  }, []);

  function startEdit() {
    setTenantId(tenant.sso_entra_tenant_id || '');
    setRequired(Boolean(tenant.sso_required));
    setError('');
    setEditing(true);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data } = await api.put(`/api/superadmin/tenants/${tenant.id}`, {
        sso_entra_tenant_id: tenantId.trim(),
        sso_required: required && Boolean(tenantId.trim()),
      });
      onSaved(data);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  const consentUrl = tenant.sso_entra_tenant_id && clientId
    ? `https://login.microsoftonline.com/${tenant.sso_entra_tenant_id}/adminconsent?client_id=${clientId}`
    : null;

  async function copyConsent() {
    try {
      await navigator.clipboard.writeText(consentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  const status = !tenant.sso_entra_tenant_id
    ? t('tenants.sso.notSet')
    : tenant.sso_required ? t('tenants.sso.enabled') : t('tenants.sso.optional');

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('tenants.sso.heading')}</h2>
        {!editing && (
          <button onClick={startEdit} className="flex items-center gap-1 text-sm text-brand-700 hover:text-brand-900 font-medium">
            <Pencil size={13} /> {t('common.edit')}
          </button>
        )}
      </div>

      {!editing ? (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <dt className="text-xs text-gray-400">{t('tenants.sso.tenantId')}</dt>
            <dd className="text-sm text-gray-800 mt-0.5 font-mono break-all">{tenant.sso_entra_tenant_id || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-400">{t('tenants.sso.required')}</dt>
            <dd className="text-sm text-gray-800 mt-0.5">{status}</dd>
          </div>
          {consentUrl && (
            <div className="sm:col-span-2">
              <dt className="text-xs text-gray-400">{t('tenants.sso.consentLink')}</dt>
              <dd className="text-sm text-gray-800 mt-0.5 flex items-start gap-2">
                <span className="font-mono text-xs break-all flex-1">{consentUrl}</span>
                <button type="button" onClick={copyConsent} className="text-xs text-brand-700 hover:text-brand-900 font-medium shrink-0">
                  {copied ? t('tenants.sso.copied') : t('tenants.sso.copy')}
                </button>
              </dd>
            </div>
          )}
        </dl>
      ) : (
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('tenants.sso.tenantId')}</label>
            <input
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2 font-mono focus:outline-none focus:border-brand-500"
            />
            <p className="text-xs text-gray-400 mt-1">{t('tenants.sso.tenantIdHint')}</p>
          </div>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={required}
              disabled={!tenantId.trim()}
              onChange={e => setRequired(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-brand-700 focus:ring-brand-600"
            />
            <span>
              <span className="block text-sm text-gray-800">{t('tenants.sso.required')}</span>
              <span className="block text-xs text-gray-400">{t('tenants.sso.requiredHint')}</span>
            </span>
          </label>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setEditing(false)} className="text-sm text-gray-500 hover:text-gray-800 px-3 py-2">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 bg-brand-700 hover:bg-brand-900 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60 transition-colors"
            >
              {saving ? <RotateCcw size={13} className="animate-spin" /> : <Check size={13} />}
              {t('common.save')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function TenantCardPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(null);
  const [shelters, setShelters] = useState([]);
  const [unackedIds, setUnackedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', contact_email: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [tenRes, shelRes, alertRes] = await Promise.all([
        api.get(`/api/superadmin/tenants/${id}`),
        api.get('/api/shelters'),
        api.get('/api/alerts?unacknowledged=true&limit=100'),
      ]);
      setTenant(tenRes.data);
      const allShelters = Array.isArray(shelRes.data) ? shelRes.data : [];
      setShelters(allShelters.filter(s => s.tenant_id === id));
      const alerts = Array.isArray(alertRes.data) ? alertRes.data : [];
      setUnackedIds(new Set(alerts.filter(a => !a.acknowledged_at).map(a => a.shelter_id)));
    } catch (err) {
      setLoadError(err.response?.data?.error || t('tenants.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  function startEdit() {
    setForm({ name: tenant.name || '', contact_email: tenant.contact_email || '' });
    setSaveError('');
    setEditing(true);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    try {
      const { data } = await api.put(`/api/superadmin/tenants/${id}`, form);
      setTenant(prev => ({ ...prev, ...data }));
      setEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function deleteTenant() {
    if (!window.confirm(t('tenants.confirmDeleteFull', { name: tenant.name }))) return;
    setDeleting(true);
    try {
      await api.delete(`/api/superadmin/tenants/${id}`);
      navigate('/dashboard');
    } finally {
      setDeleting(false);
    }
  }

  async function deleteShelter(shelterId) {
    if (!window.confirm(t('tenants.shelter.confirmDelete'))) return;
    await api.delete(`/api/superadmin/shelters/${shelterId}`);
    setShelters(s => s.filter(x => x.id !== shelterId));
  }

  if (loading) return <div className="text-center py-16 text-gray-400 text-sm">{t('common.loading')}</div>;
  if (loadError) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700 max-w-3xl">
      {loadError}
    </div>
  );
  if (!tenant) return null;

  const digiShelters = shelters.filter(s => s.safe_digi !== false);
  const serviceOnlyShelters = shelters.filter(s => s.safe_digi === false);

  return (
    <div className="max-w-3xl">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft size={14} /> {t('common.backToDashboard')}
      </Link>

      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-gray-900">{tenant.name}</h1>
        <div className="flex items-center gap-3">
          <Link
            to={`/settings/${id}`}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
            title={t('nav.alertSettings')}
          >
            <Bell size={14} /> {t('nav.alertSettings')}
          </Link>
          <button onClick={load} className="text-gray-400 hover:text-gray-700" title={t('common.refresh')}>
            <RotateCcw size={15} />
          </button>
        </div>
      </div>

      {/* Perustiedot */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('shelterPage.basicInfo')}</h2>
          {!editing && (
            <button onClick={startEdit} className="flex items-center gap-1 text-sm text-brand-700 hover:text-brand-900 font-medium">
              <Pencil size={13} /> {t('common.edit')}
            </button>
          )}
        </div>

        {!editing ? (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <dt className="text-xs text-gray-400">{t('tenants.companyName')}</dt>
              <dd className="text-sm text-gray-800 mt-0.5">{tenant.name}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400">{t('tenants.contactEmail')}</dt>
              <dd className="text-sm text-gray-800 mt-0.5">{tenant.contact_email || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400">{t('tenants.sheltersLabel')}</dt>
              <dd className="text-sm text-gray-800 mt-0.5">{shelters.length}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400">{t('tenants.usersLabel')}</dt>
              <dd className="text-sm text-gray-800 mt-0.5">{tenant.user_count ?? '—'}</dd>
            </div>
          </dl>
        ) : (
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('tenants.companyName')}</label>
                <input
                  required
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:outline-none focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('tenants.contactEmail')}</label>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:outline-none focus:border-brand-500"
                />
              </div>
            </div>

            {saveError && <p className="text-sm text-red-600">{saveError}</p>}

            <div className="flex justify-between items-center pt-2">
              <button
                type="button"
                onClick={deleteTenant}
                disabled={deleting}
                className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                {deleting ? t('tenants.deleting') : t('tenants.deleteCustomer')}
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing(false)} className="text-sm text-gray-500 hover:text-gray-800 px-3 py-2">
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-1.5 bg-brand-700 hover:bg-brand-900 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60 transition-colors"
                >
                  {saving ? <RotateCcw size={13} className="animate-spin" /> : <Check size={13} />}
                  {t('common.save')}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      <SsoSection tenant={tenant} onSaved={data => setTenant(prev => ({ ...prev, ...data }))} />

      {/* Suojatilat */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('tenants.sheltersHeading')}</h2>
        </div>
        {digiShelters.length === 0 ? (
          <p className="text-sm text-gray-400 px-4 py-4">{t('tenants.shelter.noShelters')}</p>
        ) : (
          <ShelterTable shelters={digiShelters} unackedIds={unackedIds} onReload={load} canEdit={false} onDelete={deleteShelter} />
        )}
        <AddShelterButton tenantId={id} />
      </div>

      {/* Vain huollettavat suojatilat (#82 versio 10): ei Safe Digiä, näkyvät vain Huolto-sivulla */}
      {serviceOnlyShelters.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mt-6">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('tenants.serviceOnlyHeading')}</h2>
            <p className="text-xs text-gray-400 mt-1">{t('tenants.serviceOnlyHelp')}</p>
          </div>
          <ul className="divide-y divide-gray-100">
            {serviceOnlyShelters.map(s => (
              <li key={s.id} className="flex items-center gap-3 px-4 py-2.5">
                <Link to={`/suojatila/${s.id}`} className="text-sm font-medium text-gray-800 hover:text-brand-700 hover:underline truncate">
                  {s.name}
                </Link>
                <span className="text-xs text-gray-400 truncate">{[s.location, s.serial_number].filter(Boolean).join(' · ')}</span>
                <button onClick={() => deleteShelter(s.id)} className="ml-auto text-xs text-red-600 hover:text-red-800 shrink-0">
                  {t('common.delete')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
